"use client";

// Leave space for multipart metadata below Vercel's 4.5 MB request-body limit.
export const SERVER_PHOTO_MAX_BYTES = 4_000_000;
const RESIZABLE_PHOTO_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export async function prepareServerPhoto(file: File): Promise<File> {
  if (file.size <= SERVER_PHOTO_MAX_BYTES || !RESIZABLE_PHOTO_TYPES.has(file.type)) {
    return file;
  }

  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();
  try {
    image.src = sourceUrl;
    await image.decode();
    const scale = Math.min(1, 4096 / Math.max(image.naturalWidth, image.naturalHeight));
    let width = Math.max(1, Math.round(image.naturalWidth * scale));
    let height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Photo processing is unavailable");
    }

    for (let attempt = 0; attempt < 8; attempt++) {
      canvas.width = width;
      canvas.height = height;
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);
      const jpeg = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/jpeg", 0.9));
      if (!jpeg || jpeg.size === 0) {
        throw new Error("Photo processing returned an empty image");
      }
      if (jpeg.size <= SERVER_PHOTO_MAX_BYTES) {
        const name = file.name.replace(/\.[^.]+$/, "") || "photo";
        return new File([jpeg], `${name}.jpg`, { type: "image/jpeg", lastModified: file.lastModified });
      }
      width = Math.max(1, Math.floor(width * 0.75));
      height = Math.max(1, Math.floor(height * 0.75));
    }
    throw new Error("Photo could not fit the upload limit");
  } catch (error) {
    throw new Error("We couldn't prepare this photo. Please try a smaller JPEG or PNG.", { cause: error });
  } finally {
    image.src = "";
    URL.revokeObjectURL(sourceUrl);
  }
}
